import { useTranslation } from 'react-i18next';

function triggerAIAgent() {
  const btn = document.querySelector<HTMLButtonElement>('#vapi-widget-floating-button');
  if (btn) btn.click();
}

export default function HeroSection() {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center bg-background-50 overflow-hidden">
      {/* Subtle abstract background pattern */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-primary-100/30 blur-3xl -translate-y-1/4 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-accent-100/20 blur-3xl translate-y-1/4 -translate-x-1/4"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,oklch(var(--background-100)_/_0.3),transparent_70%)]"></div>
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 pt-24 md:pt-32 pb-12">
        {/* Title Block */}
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight tracking-tight text-foreground-950">
            {t('hero.title_line1')}
            <br />
            {t('hero.title_line2')}
            <br />
            <span className="text-primary-500">{t('hero.title_line3')}</span>
          </h1>

          <p className="mt-6 md:mt-8 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={triggerAIAgent}
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              {t('hero.cta_demo')}
              <i className="ri-calendar-line text-lg"></i>
            </button>
            <a
              href="https://wa.me/201099289280"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 text-white px-6 py-3 rounded-full text-sm md:text-base font-semibold hover:bg-green-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              {t('hero.cta_sales')}
            </a>
          </div>
        </div>

        {/* Product Cards - Staggered Layout */}
        <div className="relative mt-14 md:mt-20 flex items-end justify-center gap-4 md:gap-6 max-w-5xl mx-auto">
          {/* Left Card - lower */}
          <div className="relative w-[28%] md:w-[30%] transform translate-y-6 md:translate-y-8 -rotate-1 z-10">
            <div className="aspect-[4/5] rounded-xl overflow-hidden bg-white border border-background-200/60">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20textile%20factory%20interior%20with%20rows%20of%20weaving%20machines%20in%20operation%2C%20warm%20industrial%20lighting%2C%20clean%20organized%20production%20floor%2C%20minimalist%20industrial%20aesthetic%2C%20bokeh%20background%20with%20fabric%20rolls%2C%20professional%20manufacturing%20environment%2C%20soft%20natural%20light%20from%20windows%2C%20high%20detail%20editorial%20photography&width=600&height=750&seq=hero-card-left&orientation=portrait"
                alt="Textile production floor"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
          </div>

          {/* Center Card - highest, most prominent */}
          <div className="relative w-[36%] md:w-[34%] z-20">
            <div className="aspect-[4/5] rounded-xl overflow-hidden bg-white border border-background-200/60 ring-1 ring-primary-200/40">
              <img
                src="https://readdy.ai/api/search-image?query=Enterprise%20ERP%20dashboard%20displayed%20on%20modern%20computer%20screen%20in%20textile%20factory%20control%20room%2C%20showing%20production%20metrics%20and%20analytics%20charts%2C%20warm%20amber%20accent%20colors%2C%20clean%20modern%20interface%2C%20factory%20floor%20visible%20through%20glass%20windows%20in%20background%2C%20professional%20corporate%20environment%2C%20soft%20natural%20lighting%2C%20high%20detail%20editorial%20photography&width=680&height=850&seq=hero-card-center&orientation=portrait"
                alt="ERP dashboard"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent"></div>
              <div className="absolute top-3 left-3 bg-primary-500 text-background-50 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                Live Dashboard
              </div>
            </div>
          </div>

          {/* Right Card - lower */}
          <div className="relative w-[28%] md:w-[30%] transform translate-y-6 md:translate-y-8 rotate-1 z-10">
            <div className="aspect-[4/5] rounded-xl overflow-hidden bg-white border border-background-200/60">
              <img
                src="https://readdy.ai/api/search-image?query=Textile%20warehouse%20with%20organized%20fabric%20rolls%20stacked%20on%20shelves%2C%20modern%20inventory%20management%20system%20with%20barcode%20scanner%2C%20clean%20bright%20industrial%20space%2C%20warm%20ambient%20lighting%2C%20professional%20logistics%20environment%2C%20minimalist%20aesthetic%2C%20high%20detail%20photography%20with%20natural%20tones&width=600&height=750&seq=hero-card-right&orientation=portrait"
                alt="Textile warehouse"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}